// @flow
import {Cookies} from 'react-cookie';
import {equals, filter, includes, isNil, map, reduce} from 'ramda';
import {getPermissions} from './configUtils';
import {CACHE_KEYS} from './constants/cacheKey';
import {apiClient} from './api';

/**
 * Checks if user is authenticated (based on session cookie flag)
 */
const isUserAuthenticated = () => {
    const cookies = new Cookies();
    const user = cookies.get(CACHE_KEYS.USER);
    return !!user;
};

/**
 * Returns the logged in user session info (lightweight, no token)
 */
const getLoggedInUser = () => {
    const cookies = new Cookies();
    const user = cookies.get(CACHE_KEYS.USER);
    return user ? (typeof user == 'object' ? user : JSON.parse(user)) : null;
};

/**
 * Get the access token from memory.
 * Used by logout and any code that needs the raw token.
 */
const getAccessToken = () => {
    return apiClient.getToken() || '';
};

/**
 * Get user profile from Redux store.
 * This is a placeholder — components should use Redux connect() or useSelector() instead.
 * Kept for backward compatibility where getUserFromToken() was called directly.
 */
const getUserFromToken = () => {
    // This function can no longer decode JWT (token is in memory, not cookie).
    // Components should read from Redux state.Auth.userInfo instead.
    // Return empty object for backward compatibility.
    return {};
};

const hasPermission = (requiredPermissions) => {
    let result = isNil(requiredPermissions);
    const permissions = getPermissions();
    const userPerm = parseInt(sessionStorage.getItem(CACHE_KEYS.USER_PERMISSION) || '0', 10);

    if (requiredPermissions) {
        requiredPermissions.forEach(group => {
            const isIncluded = x => includes(x.value, group.name);
            const idx = map(a => parseInt(a.key), filter(isIncluded, permissions));
            const required = reduce((a, b) => a | b, 0, idx);
            if (required === 0 || (userPerm & required) === required) {
                result = true;
                return result;
            }
        });
    }

    return result;
};

const getPermissionNamesFromGroup = (groupPermission) => {
    return getPermissions().filter(item => equals(item.key, groupPermission & item.key)).map(item => item.value);
};

const getFoldersPermissions = (mapParentFolder: boolean = true) => {
    let folders = sessionStorage.getItem(CACHE_KEYS.FOLDERS_PERMISSIONS);
    if (folders) {
        folders = JSON.parse(folders);
        let allFolders = []
        folders.forEach(folder => {
            folder.children.forEach(f => {
                f.parentLabel = folder.label;
                allFolders.push(f)
            });
            allFolders.push(folder);
        });
        return allFolders;
    }
    return [];
};

const hasFullControlPermission = permissions => {
    return includes('Document.FullControl', permissions);
};

export {
    isUserAuthenticated,
    getLoggedInUser,
    getUserFromToken,
    getAccessToken,
    hasPermission,
    getPermissionNamesFromGroup,
    getFoldersPermissions,
    hasFullControlPermission,
};
